// 設定画面: Google Drive 連携、辞書管理、再生設定、ストレージ使用量の表示。
import { storageEstimate } from '../lib/opfs.js'
import { listDicts, setDictEnabled, deleteDict, installFromFile, fetchDictCatalog, downloadCloudDict } from '../lib/dicts.js'
import { isPersisted, requestPersist, isStandalone, describeError } from '../lib/pwa.js'
import { getPrefs, setPref } from '../lib/playerprefs.js'
import { getSyncPrefs, setSyncPref } from '../lib/syncprefs.js'
import { autoUploadAll } from '../lib/drivepush.js'
import { icon } from '../lib/icons.js'
import { t, getLang, setLang } from '../lib/i18n.js'
import { isPurchased, setPurchased, acquiredCount, resetAcquired, FREE_LIMIT } from '../lib/entitlements.js'
import {
  connect as gdriveConnect,
  fetchCatalog as fetchGDriveCatalog,
} from '../lib/gdrive.js'

const LANGS = [
  { v: 'ja', label: '日本語' },
  { v: 'en', label: 'English' },
]

function escapeHtml(s) {
  return String(s).replace(/[&<>"]/g, (c) => ({ '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;' }[c]))
}

function fmtBytes(n) {
  if (n == null) return '—'
  const u = ['B', 'KB', 'MB', 'GB']
  let i = 0
  while (n >= 1024 && i < u.length - 1) {
    n /= 1024
    i++
  }
  return `${n.toFixed(1)} ${u[i]}`
}

export async function renderSettings(root) {
  const est = await storageEstimate()
  const pp = await getPrefs()
  const sp = await getSyncPrefs()

  root.innerHTML = `
    <h1>${t('settings.title')}</h1>
    <div class="card">
      <div style="font-weight:600;margin-bottom:8px">${t('settings.lang.title')}</div>
      <div class="field">
        <label for="f-lang">${t('settings.lang.label')}</label>
        <select id="f-lang">
          ${LANGS.map((l) => `<option value="${l.v}" ${l.v === getLang() ? 'selected' : ''}>${l.label}</option>`).join('')}
        </select>
      </div>
    </div>

    <div class="card">
      <div style="font-weight:600;margin-bottom:4px">Google Drive（メディアを自分の Drive から受け取る）</div>
      <div class="muted" style="font-size:13px;margin-bottom:14px">
        PC で取り込んだ素材を、あなた自身の Google Drive から受け取ります。
        「連携する」を押して Google アカウントでログインしてください。
      </div>
      <div class="row">
        <button class="btn secondary" id="btn-gdrive-connect">連携する</button>
        <button class="btn secondary" id="btn-gdrive-test">カタログ取得テスト</button>
      </div>
      <div class="status" id="gdrive-status"></div>
    </div>

    <div class="card">
      <div style="font-weight:600;margin-bottom:4px">${t('sync.title')}</div>
      <div class="muted" style="font-size:13px;margin-bottom:12px">${t('sync.cardDesc')}</div>
      <div class="field">
        <label for="f-autoupload">${t('sync.autoUpload')}</label>
        <select id="f-autoupload">
          <option value="wifi" ${sp.autoUpload === 'wifi' ? 'selected' : ''}>${t('sync.autoUpload.wifi')}</option>
          <option value="always" ${sp.autoUpload === 'always' ? 'selected' : ''}>${t('sync.autoUpload.always')}</option>
        </select>
      </div>
      <div class="field">
        <label for="f-download">${t('sync.download')}</label>
        <select id="f-download">
          <option value="wifi" ${sp.download === 'wifi' ? 'selected' : ''}>${t('sync.download.wifi')}</option>
          <option value="stream" ${sp.download === 'stream' ? 'selected' : ''}>${t('sync.download.stream')}</option>
        </select>
      </div>
    </div>

    <div class="card">
      <div style="font-weight:600;margin-bottom:4px">${t('settings.dict.title')}</div>
      <div class="muted" style="font-size:13px;margin-bottom:10px">${t('settings.dict.desc2')}</div>
      <div id="dict-list"></div>
      <div class="row" style="margin-top:10px">
        <button class="btn secondary" id="btn-dict-cloud">${t('settings.dict.cloud')}</button>
        <button class="btn secondary" id="btn-dict-import">${t('settings.dict.import')}</button>
      </div>
      <input type="file" id="dict-file" accept=".ndjson,.jsonl,.tsv,.txt" style="position:absolute;width:1px;height:1px;opacity:0;pointer-events:none">
      <div class="item-progress" id="dict-prog" hidden><div class="bar"><span></span></div><span class="pct"></span></div>
      <div class="status" id="dict-status"></div>
    </div>

    <div class="card">
      <div style="font-weight:600;margin-bottom:8px">${t('settings.playback')}</div>
      <div class="field">
        <label for="f-skip">${t('settings.skipLabel', { icons: `<span class="lbl-ico">${icon('rewind')}${icon('forward')}</span>` })}</label>
        <input id="f-skip" type="number" min="1" max="60" step="1" value="${pp.skipSeconds}" />
      </div>
    </div>

    <div class="card">
      <div style="font-weight:600;margin-bottom:8px">${t('settings.subStyle.title')}</div>
      <div class="subprev" id="subprev"><span id="subprev-line">Subtitle</span></div>
      <div class="field">
        <label>${t('settings.subStyle.size')}</label>
        <div class="stepper"><button id="ss-size-down">−</button><span id="ss-size-val">${pp.subSize}px</span><button id="ss-size-up">＋</button></div>
      </div>
      <div class="field">
        <label for="ss-weight">${t('settings.subStyle.weight')}</label>
        <select id="ss-weight">
          <option value="400" ${Number(pp.subWeight) === 400 ? 'selected' : ''}>${t('settings.subStyle.weightNormal')}</option>
          <option value="700" ${Number(pp.subWeight) !== 400 ? 'selected' : ''}>${t('settings.subStyle.weightBold')}</option>
        </select>
      </div>
      <div class="field">
        <label for="ss-color">${t('settings.subStyle.color')}</label>
        <select id="ss-color">
          <option value="#ffe14d" ${pp.subColor === '#ffe14d' ? 'selected' : ''}>${t('settings.subStyle.colorYellow')}</option>
          <option value="#ffffff" ${pp.subColor === '#ffffff' ? 'selected' : ''}>${t('settings.subStyle.colorWhite')}</option>
          <option value="#7fe3ff" ${pp.subColor === '#7fe3ff' ? 'selected' : ''}>${t('settings.subStyle.colorCyan')}</option>
          <option value="#a6f0a6" ${pp.subColor === '#a6f0a6' ? 'selected' : ''}>${t('settings.subStyle.colorGreen')}</option>
        </select>
      </div>
      <div class="field">
        <label for="ss-outline">${t('settings.subStyle.outline')}</label>
        <select id="ss-outline">
          <option value="on" ${pp.subOutline === false ? '' : 'selected'}>${t('settings.subStyle.outlineOn')}</option>
          <option value="off" ${pp.subOutline === false ? 'selected' : ''}>${t('settings.subStyle.outlineOff')}</option>
        </select>
      </div>
    </div>

    <div class="card">
      <div style="font-weight:600;margin-bottom:8px">${t('settings.storage')}</div>
      <div class="muted" style="font-size:13px">
        ${t('settings.usage', { usage: fmtBytes(est?.usage), quota: fmtBytes(est?.quota) })}
      </div>
      <div class="muted" style="font-size:13px;margin-top:6px" id="persist-info"></div>
      <button class="btn secondary" id="btn-persist" style="margin-top:10px" hidden>${t('settings.persist.enable')}</button>
    </div>

    <div class="card">
      <div style="font-weight:600;margin-bottom:4px">開発者：購入状態（ダミー）</div>
      <div class="muted" style="font-size:13px;margin-bottom:10px">
        3本無料ゲートの動作確認用。実課金は未実装（ネイティブの StoreKit ＋ Web/RevenueCat で差し替え）。
      </div>
      <div class="field">
        <label for="f-purchased">購入状態</label>
        <select id="f-purchased">
          <option value="free" ${isPurchased() ? '' : 'selected'}>無料（3本まで）</option>
          <option value="purchased" ${isPurchased() ? 'selected' : ''}>購入済み（無制限）</option>
        </select>
      </div>
      <div class="row">
        <span class="muted" style="font-size:13px">無料枠（累積）: <span id="acq-count">…</span> / ${FREE_LIMIT}</span>
        <button class="btn secondary" id="btn-acq-reset">カウントをリセット</button>
      </div>
    </div>
  `

  // 言語切替（保存して location.reload() で全ビューを描き直す）。
  root.querySelector('#f-lang').addEventListener('change', (e) => setLang(e.target.value))

  // 開発者：購入状態の切替（保存して reload＝全ゲートを再評価）。
  root.querySelector('#f-purchased').addEventListener('change', (e) => {
    setPurchased(e.target.value === 'purchased')
    location.reload()
  })
  // 開発者：無料カウント（累積）の表示＋リセット。
  const acqEl = root.querySelector('#acq-count')
  acquiredCount().then((n) => { acqEl.textContent = n })
  root.querySelector('#btn-acq-reset').addEventListener('click', async () => {
    resetAcquired()
    acqEl.textContent = await acquiredCount()
  })

  // --- Google Drive 連携（受け取り・最小疎通：連携＋カタログ取得の確認） ---
  const gdStatus = root.querySelector('#gdrive-status')
  const setGd = (msg, cls = '') => {
    gdStatus.textContent = msg
    gdStatus.className = 'status ' + cls
  }
  root.querySelector('#btn-gdrive-connect').addEventListener('click', async () => {
    setGd('連携中… ポップアップで許可してください')
    try {
      await gdriveConnect()
      setGd('連携しました', 'ok')
      autoUploadAll().then((r) => { if (r && r.ok && r.uploaded > 0) setGd(t('local.uploadedN', { n: r.uploaded }), 'ok') }) // 連携直後バックフィル
    } catch (e) {
      setGd('連携失敗: ' + (e.message || e), 'err')
    }
  })
  root.querySelector('#btn-gdrive-test').addEventListener('click', async () => {
    setGd('カタログ取得中…')
    try {
      const cat = await fetchGDriveCatalog()
      const n = Object.keys(cat.items || {}).length
      if (cat._empty) setGd('catalog.json が見つかりません（PC から push 済みか確認）', 'err')
      else setGd(`成功: ${n} 件の項目が見えます`, 'ok')
    } catch (e) {
      setGd('失敗: ' + (e.message || e), 'err')
    }
  })

  // --- 同期設定（Drive 双方向の土台。挙動の接続は次フェーズ） ---
  root.querySelector('#f-autoupload').addEventListener('change', (e) => setSyncPref('autoUpload', e.target.value))
  root.querySelector('#f-download').addEventListener('change', (e) => setSyncPref('download', e.target.value))

  // --- 再生設定 ---
  root.querySelector('#f-skip').addEventListener('change', async (e) => {
    const v = Math.max(1, Math.min(60, Number(e.target.value) || 5))
    e.target.value = v
    await setPref('skipSeconds', v)
  })

  // --- 字幕の見た目（変更は次にプレーヤを開いたときに反映。ここはライブプレビュー） ---
  const ssState = { subSize: pp.subSize, subWeight: pp.subWeight, subColor: pp.subColor, subOutline: pp.subOutline }
  const prevEl = root.querySelector('#subprev')
  const prevLine = root.querySelector('#subprev-line')
  const sizeVal = root.querySelector('#ss-size-val')
  const applyPreview = () => {
    prevLine.style.fontSize = ssState.subSize + 'px'
    prevLine.style.fontWeight = ssState.subWeight
    prevLine.style.color = ssState.subColor
    prevLine.style.textShadow = ssState.subOutline === false ? 'none' : '0 2px 6px rgba(0,0,0,0.95), 0 0 3px rgba(0,0,0,0.95)'
    sizeVal.textContent = ssState.subSize + 'px'
  }
  applyPreview()
  root.querySelector('#ss-size-down').addEventListener('click', async () => {
    ssState.subSize = Math.max(14, ssState.subSize - 2); applyPreview(); await setPref('subSize', ssState.subSize)
  })
  root.querySelector('#ss-size-up').addEventListener('click', async () => {
    ssState.subSize = Math.min(44, ssState.subSize + 2); applyPreview(); await setPref('subSize', ssState.subSize)
  })
  root.querySelector('#ss-weight').addEventListener('change', async (e) => {
    ssState.subWeight = Number(e.target.value); applyPreview(); await setPref('subWeight', ssState.subWeight)
  })
  root.querySelector('#ss-color').addEventListener('change', async (e) => {
    ssState.subColor = e.target.value; applyPreview(); await setPref('subColor', ssState.subColor)
  })
  root.querySelector('#ss-outline').addEventListener('change', async (e) => {
    ssState.subOutline = e.target.value === 'on'; applyPreview(); await setPref('subOutline', ssState.subOutline)
  })

  // --- 永続化状態 ---
  const persistInfo = root.querySelector('#persist-info')
  const persistBtn = root.querySelector('#btn-persist')
  const refreshPersist = async () => {
    const ok = await isPersisted()
    persistInfo.innerHTML = ok
      ? `${t('settings.persist.label')}: <span style="color:var(--ok)">${t('settings.persist.enabled')}</span>${t('settings.persist.onNote')}`
      : `${t('settings.persist.label')}: <span style="color:var(--err)">${t('settings.persist.disabled')}</span>` +
        (isStandalone() ? '' : `<br>${t('settings.persist.offNote')}`)
    persistBtn.hidden = ok
  }
  refreshPersist()
  persistBtn.addEventListener('click', async () => {
    await requestPersist()
    await refreshPersist()
  })

  // --- 辞書（複数管理） ---
  const dictList = root.querySelector('#dict-list')
  const dictStatus = root.querySelector('#dict-status')
  const dictProg = root.querySelector('#dict-prog')
  const dictFile = root.querySelector('#dict-file')
  const setDictStatus = (msg, cls = '') => { dictStatus.className = 'status ' + cls; dictStatus.textContent = msg }

  // インストール済み辞書の一覧（ON/OFF・削除）。
  async function renderDictList() {
    const dicts = await listDicts()
    if (!dicts.length) { dictList.innerHTML = `<div class="muted" style="font-size:13px">${t('settings.dict.none')}</div>`; return }
    dictList.innerHTML = dicts.map((d) => `
      <div class="dict-row" data-id="${d.id}">
        <label class="dict-toggle"><input type="checkbox" ${d.enabled !== false ? 'checked' : ''} data-act="toggle"><span></span></label>
        <div class="dict-meta">
          <div class="dict-name">${escapeHtml(d.name)}</div>
          <div class="muted" style="font-size:12px">${t('settings.dict.words', { n: (d.count || 0).toLocaleString() })}${d.license ? ' ・ ' + escapeHtml(d.license) : ''}</div>
        </div>
        <button class="mini danger" data-act="del">${t('common.remove')}</button>
      </div>`).join('')
  }
  await renderDictList()

  dictList.addEventListener('click', async (ev) => {
    const row = ev.target.closest('.dict-row'); if (!row) return
    const id = row.dataset.id
    if (ev.target.closest('[data-act="del"]')) {
      const name = row.querySelector('.dict-name').textContent
      if (await confirmDict(t('settings.dict.deleteConfirm', { name }))) { await deleteDict(id); await renderDictList() }
    }
  })
  dictList.addEventListener('change', async (ev) => {
    const cb = ev.target.closest('[data-act="toggle"]'); if (!cb) return
    const id = ev.target.closest('.dict-row').dataset.id
    await setDictEnabled(id, cb.checked)
  })

  // 進捗つきで取り込みを走らせる共通処理。
  const runInstall = async (fn) => {
    dictProg.hidden = false
    const bar = dictProg.querySelector('.bar > span'); const pct = dictProg.querySelector('.pct')
    setDictStatus('')
    try {
      const meta = await fn((p) => {
        const frac = p.total ? p.loaded / p.total : 0
        bar.style.width = `${Math.round(frac * 100)}%`
        pct.textContent = p.total ? `${fmtBytes(p.loaded)} / ${fmtBytes(p.total)}` : `${(p.count || 0).toLocaleString()} 語`
      })
      setDictStatus(t('settings.dict.added', { name: meta.name, n: (meta.count || 0).toLocaleString() }), 'ok')
      await renderDictList()
    } catch (err) {
      setDictStatus(describeError(err), 'err')
    } finally {
      dictProg.hidden = true
    }
  }

  // ファイルから取り込む。
  root.querySelector('#btn-dict-import').addEventListener('click', () => dictFile.click())
  dictFile.addEventListener('change', async () => {
    const f = (dictFile.files || [])[0]; dictFile.value = ''
    if (!f) return
    await runInstall((onP) => installFromFile(f, f.name.replace(/\.[^.]+$/, '')).then((m) => { onP({ done: true, count: m.count, total: 0 }); return m }))
  })

  // クラウド配布カタログから選んで取り込む。
  root.querySelector('#btn-dict-cloud').addEventListener('click', async () => {
    setDictStatus(t('settings.dict.loadingCatalog'))
    let cat = []
    try { cat = await fetchDictCatalog() } catch { /* noop */ }
    if (!cat.length) { setDictStatus(t('settings.dict.catalogEmpty'), 'err'); return }
    setDictStatus('')
    const entry = await pickCatalog(cat)
    if (!entry) return
    await runInstall((onP) => downloadCloudDict(entry, onP))
  })

  // 簡易ピッカー / 確認のオーバーレイ（paywall スタイルを流用）。
  function pickCatalog(cat) {
    return new Promise((resolve) => {
      const ov = document.createElement('div')
      ov.className = 'paywall-overlay sheet-overlay'
      const sub = (c) => [c.note, c.size ? fmtBytes(c.size) : '', c.license].filter(Boolean).map(escapeHtml).join(' ・ ')
      ov.innerHTML = `<div class="menu-sheet"><div class="menu-head">${t('settings.dict.cloud')}</div>` +
        cat.map((c, i) => `<button class="menu-item" data-i="${i}">${icon('library')}<span>${escapeHtml(c.name)}${c.recommended ? ' ' + t('settings.dict.recommended') : ''}</span>` +
          `<div class="menu-sub muted">${sub(c)}</div></button>`).join('') +
        `<button class="btn secondary" data-i="-1">${t('common.close')}</button></div>`
      const close = (v) => { ov.remove(); resolve(v) }
      ov.addEventListener('click', (e) => {
        if (e.target === ov) return close(null)
        const b = e.target.closest('button[data-i]'); if (!b) return
        const i = Number(b.dataset.i)
        close(i >= 0 ? cat[i] : null)
      })
      document.body.appendChild(ov)
    })
  }
  function confirmDict(msg) {
    return new Promise((resolve) => {
      const ov = document.createElement('div')
      ov.className = 'paywall-overlay'
      ov.innerHTML = `<div class="paywall"><div class="paywall-desc">${escapeHtml(msg)}</div>` +
        `<button class="btn danger" data-s="ok">${t('common.remove')}</button>` +
        `<button class="btn secondary" data-s="cancel">${t('common.close')}</button></div>`
      const close = (v) => { ov.remove(); resolve(v) }
      ov.addEventListener('click', (e) => {
        if (e.target === ov) return close(false)
        const b = e.target.closest('button[data-s]'); if (!b) return
        close(b.dataset.s === 'ok')
      })
      document.body.appendChild(ov)
    })
  }
}
